import { useQuery } from "@tanstack/react-query";
import { RefreshCwIcon, RotateCcwIcon } from "lucide-react";
import { useState } from "react";

import {
  ChartPanel,
  DashboardFilterField,
  DashboardFilterRow,
  DashboardSelect,
  DashboardsPageFrame,
  DataTable,
  dashboardControlClassName,
} from "./dashboardKit";
import { calKillerSnapshotQueryOptions } from "./calKillerReactQuery";
import type {
  CalKillerClassRow,
  CalKillerEventTypeRow,
  CalKillerFilters,
  CalKillerResponse,
} from "./calKillerTypes";

/** ISO date in the dashboard's timezone, so "today" matches the server's day boundary. */
function dashboardDate(date: Date): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" }).format(date);
}

/** Defaults to bookings created since August 1 of the most recent August. */
function defaultFilters(): CalKillerFilters {
  const today = dashboardDate(new Date());
  const [year, month] = today.split("-");
  const augustYear = Number(month) >= 8 ? Number(year) : Number(year) - 1;
  return { dateStart: `${augustYear}-08-01`, dateEnd: today };
}

function fmtDateTime(value: string | null | undefined, timezone = "America/New_York"): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: timezone,
  }).format(date);
}

/** Formats a plain YYYY-MM-DD without re-interpreting it in the local timezone. */
function fmtDate(value: string): string {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(
    new Date(year, month - 1, day),
  );
}

function prettyLabel(value: string | null | undefined, fallback: string): string {
  if (!value) return fallback;
  return value.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function eventTypeFilterValue(row: CalKillerEventTypeRow): string {
  return row.eventTypeSlug ?? "__blank__";
}

function eventTypeClassLabel(value: CalKillerEventTypeRow["eventTypeClass"]): string {
  if (value === "canonical") return "Canonical";
  if (value === "custom") return "Custom";
  if (value === "legacy") return "Legacy";
  return "Blank / unknown";
}

function filterValue(filters: CalKillerFilters, key: keyof CalKillerFilters): string {
  return filters[key] ?? "";
}

/** Order-independent identity for a filter set, for the Apply button's dirty check. */
function filtersKey(filters: CalKillerFilters): string {
  return JSON.stringify(Object.entries(filters).sort(([a], [b]) => a.localeCompare(b)));
}

function DateFilter(props: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <DashboardFilterField label={props.label}>
      <input
        type="date"
        value={props.value}
        onChange={(event) => props.onChange(event.target.value)}
        className={`${dashboardControlClassName} min-w-[10.5rem]`}
      />
    </DashboardFilterField>
  );
}

function Filters(props: {
  data: CalKillerResponse | null;
  filters: CalKillerFilters;
  isDirty: boolean;
  onApply: () => void;
  onChange: (key: keyof CalKillerFilters, value: string) => void;
  onReset: () => void;
}) {
  const { data, filters } = props;
  return (
    <section className="rounded-lg border border-border/70 bg-background/70 p-4 shadow-xs">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-foreground">Booking filters</h2>
          <p className="text-xs text-muted-foreground">
            Creation and scheduled dates are interpreted in {data?.timezone ?? "America/New_York"}.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={props.onReset}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <RotateCcwIcon className="size-3.5" aria-hidden />
            Reset filters
          </button>
          <button
            type="button"
            onClick={props.onApply}
            disabled={!props.isDirty}
            className="inline-flex items-center gap-1.5 rounded-md bg-foreground px-2.5 py-1.5 text-xs font-medium text-background hover:opacity-90 disabled:opacity-40"
          >
            Apply filters
          </button>
        </div>
      </div>
      <DashboardFilterRow>
        <DateFilter
          label="Created from"
          value={filterValue(filters, "dateStart")}
          onChange={(value) => props.onChange("dateStart", value)}
        />
        <DateFilter
          label="Created through"
          value={filterValue(filters, "dateEnd")}
          onChange={(value) => props.onChange("dateEnd", value)}
        />
        <DateFilter
          label="Scheduled from"
          value={filterValue(filters, "scheduledStart")}
          onChange={(value) => props.onChange("scheduledStart", value)}
        />
        <DateFilter
          label="Scheduled through"
          value={filterValue(filters, "scheduledEnd")}
          onChange={(value) => props.onChange("scheduledEnd", value)}
        />
        <DashboardFilterField label="Status">
          <DashboardSelect
            value={filterValue(filters, "status")}
            onChange={(event) => props.onChange("status", event.target.value)}
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="cancelled">Canceled</option>
            <option value="no_show">No-show</option>
          </DashboardSelect>
        </DashboardFilterField>
        <DashboardFilterField label="Event type slug">
          <DashboardSelect
            value={filterValue(filters, "eventTypeSlug")}
            onChange={(event) => props.onChange("eventTypeSlug", event.target.value)}
          >
            <option value="">All event types</option>
            <option value="__blank__">(blank / unknown)</option>
            {data?.eventTypes.map((row) => (
              <option
                key={`${row.eventTypeId ?? "blank"}:${row.eventTypeSlug ?? "blank"}`}
                value={eventTypeFilterValue(row)}
              >
                {row.eventTypeLabel} · {row.eventTypeId ?? "no ID"}
              </option>
            ))}
          </DashboardSelect>
        </DashboardFilterField>
        <DashboardFilterField label="Organization">
          <DashboardSelect
            value={filterValue(filters, "organizationId")}
            onChange={(event) => props.onChange("organizationId", event.target.value)}
          >
            <option value="">All organizations</option>
            {data?.organizations
              .filter((row) => row.organizationId)
              .map((row) => (
                <option key={row.organizationId} value={row.organizationId ?? ""}>
                  {row.organizationName}
                </option>
              ))}
          </DashboardSelect>
        </DashboardFilterField>
        <DashboardFilterField label="Organizer">
          <DashboardSelect
            value={filterValue(filters, "organizerId")}
            onChange={(event) => props.onChange("organizerId", event.target.value)}
          >
            <option value="">All organizers</option>
            {data?.organizers
              .filter((row) => row.organizerId !== null)
              .map((row) => (
                <option key={row.organizerId} value={String(row.organizerId)}>
                  {row.organizerName} · {row.organizerId}
                </option>
              ))}
          </DashboardSelect>
        </DashboardFilterField>
        <DashboardFilterField label="Booking kind">
          <DashboardSelect
            value={filterValue(filters, "bookingKind")}
            onChange={(event) => props.onChange("bookingKind", event.target.value)}
          >
            <option value="">All booking kinds</option>
            <option value="__blank__">(blank / legacy)</option>
            {data?.bookingKinds
              .filter((row) => row.bookingKind)
              .map((row) => (
                <option key={row.bookingKind} value={row.bookingKind ?? ""}>
                  {prettyLabel(row.bookingKind, row.label)}
                </option>
              ))}
          </DashboardSelect>
        </DashboardFilterField>
        <DashboardFilterField label="Scheduling source">
          <DashboardSelect
            value={filterValue(filters, "schedulingSource")}
            onChange={(event) => props.onChange("schedulingSource", event.target.value)}
          >
            <option value="">All sources</option>
            <option value="__blank__">(blank / legacy)</option>
            {data?.schedulingSources
              .filter((row) => row.schedulingSource)
              .map((row) => (
                <option key={row.schedulingSource} value={row.schedulingSource ?? ""}>
                  {prettyLabel(row.schedulingSource, row.label)}
                </option>
              ))}
          </DashboardSelect>
        </DashboardFilterField>
      </DashboardFilterRow>
    </section>
  );
}

function ClassTable(props: { recentCreatedSince: string; rows: CalKillerClassRow[] }) {
  return (
    <ChartPanel title="Slug classes">
      <DataTable
        rows={props.rows}
        rowKey={(row) => row.eventTypeClass}
        emptyMessage="No bookings match the current filters."
        columns={[
          {
            key: "eventTypeClass",
            label: "Class",
            render: (row) => eventTypeClassLabel(row.eventTypeClass),
            sortable: true,
            sortValue: (row) => eventTypeClassLabel(row.eventTypeClass),
          },
          { key: "bookingCount", label: "Bookings", align: "right", sortable: true },
          {
            key: "completedCount",
            label: "Completed",
            align: "right",
            definition: "Active bookings whose scheduled time has already passed.",
            sortable: true,
          },
          {
            key: "futureCount",
            label: "Future",
            align: "right",
            definition: "Active bookings scheduled after today.",
            sortable: true,
          },
          {
            key: "futureSinceCount",
            label: `Future, created since ${fmtDate(props.recentCreatedSince)}`,
            align: "right",
            definition: "Active bookings created on or after the cutoff and scheduled after today.",
            sortable: true,
          },
        ]}
      />
    </ChartPanel>
  );
}

function EventTypeTable(props: {
  recentCreatedSince: string;
  rows: CalKillerEventTypeRow[];
  timezone: string;
}) {
  return (
    <ChartPanel title="Event types by slug">
      <p className="-mt-2 mb-3 text-xs text-muted-foreground">
        One row per slug; bookings that share a slug across several Cal event-type IDs are merged.
        Custom slugs embed an organizer's name, legacy slugs are everything else outside the four
        canonical managed slugs. A booking can remain visible here even when Cal no longer exposes
        its event type.
      </p>
      <DataTable
        rows={props.rows}
        rowKey={(row, index) =>
          `${row.eventTypeId ?? "blank"}:${row.eventTypeSlug ?? "blank"}:${index}`
        }
        emptyMessage="No event types match the current filters."
        maxHeightClass="max-h-[28rem]"
        columns={[
          {
            key: "eventTypeLabel",
            label: "Slug",
            definition:
              "Slug recovered from the normalized Cal payload, including legacy payload shapes.",
            render: (row) => (
              <div>
                <p className="font-medium">{row.eventTypeLabel}</p>
                <p className="text-xs text-muted-foreground">
                  {row.eventTypeIdCount > 1
                    ? `${row.eventTypeIdCount} event type IDs`
                    : `ID: ${row.eventTypeId ?? "—"}`}
                </p>
              </div>
            ),
          },
          {
            key: "eventTypeClass",
            label: "Class",
            render: (row) => eventTypeClassLabel(row.eventTypeClass),
            sortable: true,
            sortValue: (row) => eventTypeClassLabel(row.eventTypeClass),
          },
          { key: "bookingCount", label: "Bookings", align: "right", sortable: true },
          {
            key: "completedCount",
            label: "Completed",
            align: "right",
            definition: "Active bookings whose scheduled time has already passed.",
            sortable: true,
          },
          {
            key: "futureCount",
            label: "Future",
            align: "right",
            definition: "Active bookings scheduled after today.",
            sortable: true,
          },
          {
            key: "recentCreatedCount",
            label: `Created since ${fmtDate(props.recentCreatedSince)}`,
            align: "right",
            sortable: true,
          },
          {
            key: "oldestCreatedAt",
            label: "Oldest created",
            render: (row) => fmtDateTime(row.oldestCreatedAt, props.timezone),
          },
          {
            key: "newestCreatedAt",
            label: "Latest created",
            render: (row) => fmtDateTime(row.newestCreatedAt, props.timezone),
          },
        ]}
      />
    </ChartPanel>
  );
}

export function CalKillerDashboard() {
  // Filters are edited as a draft and only hit the network on Apply; each
  // snapshot is a live aggregate query, so per-keystroke refetching is costly.
  const [draftFilters, setDraftFilters] = useState<CalKillerFilters>(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState<CalKillerFilters>(defaultFilters);
  const query = useQuery(calKillerSnapshotQueryOptions(appliedFilters));
  const data = query.data ?? null;
  const isDirty = filtersKey(draftFilters) !== filtersKey(appliedFilters);

  const updateFilter = (key: keyof CalKillerFilters, value: string) => {
    setDraftFilters((current) => {
      const next = { ...current };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
  };

  return (
    <DashboardsPageFrame className="space-y-5">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">
            Product
          </p>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Cal Killer</h1>
          <p className="mt-1 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            Booking volume and Cal integration health across canonical, custom, legacy, unknown, and
            blank event-type slugs.
          </p>
        </div>
        <div className="flex flex-col items-end gap-2 text-right">
          <button
            type="button"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-accent disabled:cursor-wait disabled:opacity-60"
          >
            <RefreshCwIcon
              className={query.isFetching ? "size-3.5 animate-spin" : "size-3.5"}
              aria-hidden
            />
            {query.isFetching ? "Refreshing…" : "Refresh"}
          </button>
          <p className="text-xs text-muted-foreground">
            {data
              ? `Refreshed ${fmtDateTime(data.generatedAt, data.timezone)}`
              : "Live aggregate query"}
          </p>
          <p className="text-xs text-muted-foreground">
            Timezone: {data?.timezone ?? "America/New_York"}
          </p>
        </div>
      </header>

      <Filters
        data={data}
        filters={draftFilters}
        isDirty={isDirty}
        onApply={() => setAppliedFilters(draftFilters)}
        onChange={updateFilter}
        onReset={() => {
          setDraftFilters(defaultFilters());
          setAppliedFilters(defaultFilters());
        }}
      />

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading Cal Killer…</p>
      ) : null}
      {query.error ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {query.error instanceof Error ? query.error.message : "Failed to load Cal Killer data"}
        </div>
      ) : null}

      {data ? (
        <>
          <EventTypeTable
            recentCreatedSince={data.recentCreatedSince}
            rows={data.eventTypes}
            timezone={data.timezone}
          />

          <ClassTable recentCreatedSince={data.recentCreatedSince} rows={data.classes} />

          <section className="rounded-lg border border-border/70 bg-muted/20 p-4 text-xs leading-relaxed text-muted-foreground">
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              Source, ownership, and access boundary
            </h2>
            <p>
              Owner: {data.owner}. Source tables are <code>cal_booking</code>,{" "}
              <code>cal_booking_carespace</code>,<code>carespace</code>, <code>organization</code>,
              and linked encounter rows in <code>activity</code>. Event-type slugs are extracted
              from historical Cal payload shapes, so custom and legacy slugs remain visible instead
              of being silently mapped to a managed kind.
            </p>
            <p className="mt-2">{data.accessBoundary}</p>
            <p className="mt-2">
              Each refresh runs a read-only aggregate query. The browser keeps a successful result
              fresh for five minutes. The server refresh cadence is {data.refreshIntervalMinutes}{" "}
              minutes; use Refresh to request a new snapshot. Latest source-row update:{" "}
              {fmtDateTime(data.latestDataUpdatedAt, data.timezone)}.
            </p>
          </section>
        </>
      ) : null}
    </DashboardsPageFrame>
  );
}
